import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
 ChangeDetectionStrategy,
} from '@angular/core';
import { WorkspaceService } from './workspace.service';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { AppService } from '../app.service';
import { GlobalService } from '../global/services/global.service';
import { Router } from '@angular/router';
import { TabGridLoaderService } from '../core/grid/services/tab-grid-loader.service';
import { GtwSnackbarService } from '../shared/_services/gtw-snackbar.service';

/**
 * Shell-level legacy filter panels. Mapped by screen link (with the
 * `workspace.` prefix stripped, same way `workspace-screen.component.ts` does).
 * The matching component tag is rendered in `workspace.component.html` next to
 * `<gtw-data-filters>`, so it stays visible regardless of the grid-hidden
 * overlay that the screen's `<router-outlet>` gets when there are no records.
 */
const LEGACY_FILTER_PANELS: Record<string, string> = {
  'submissions-dasboard.list': 'gtw-submission-list-filter',
};

@Component({
    selector: 'gtw-workspace',
    templateUrl: './workspace.component.html',
    styleUrls: ['./workspace.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkspaceComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output('init-scrolltab') initScrollTabEvent = new EventEmitter<any>();
  @ViewChild('tabContainer') tabContainerRef!: ElementRef;

  showFilters = false;
  isEnable = false;
  backMessage = 'Back To Previous';
  workspaces: any = [];
  state: boolean = false;
  dataFetched = false;
  isCeScreen = false;
  gridLoading = false;
  hideDataFilters = false;
  legacyFilterTag: string = '';

  // Scrollable tabset state
  disableScrollLeft = true;
  disableScrollRight = true;
  hideScrollButtons = true;
  hideDropDown = true;
  showDropdownMenu = false;
  dropdownTabs: any[] = [];
  private scrollIntervalId: any = null;
  private isScrollHolding = false;
  private scrollByPixels = 50;
  private winResizeTimeout: any;
  private resizeHandler = () => this.onWindowResize();

  private readonly subs = new Subscription();

  constructor(
    private workspaceFactory: WorkspaceService,
    private appService: AppService,
    private globalService: GlobalService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private tabGridLoader: TabGridLoaderService,
    private gtwSnackbarService: GtwSnackbarService
  ) {}

  ngOnInit(): void {
    this.onWorkspaceLoad();

    if (
      this.workspaceFactory.workspaceFactory.goback.workspace &&
      !this.workspaceFactory.workspaceFactory.workflowObj
    ) {
      this.backMessage = this.workspaceFactory.workspaceFactory.goback.backMessage;
      this.isEnable = true;
    }

    // Background-tab error toast: per-tab `results$` only has a subscriber
    // while the corresponding tab is active, so an error landing on a tab
    // the user has navigated away from would otherwise be silent. Errors
    // for the *active* tab are still surfaced by `GridDirective` (so the
    // toast appears alongside the cleared grid loader); we filter those out
    // here to avoid duplicate snackbars.
    this.subs.add(
      this.tabGridLoader.errors$.subscribe(({ screen }) => {
        if (!screen) return;
        const activeKey = this.workspaceFactory.workspaceFactory.activeScreen?.screen_key;
        if (String(screen.screen_key) === String(activeKey)) return;
        const label = screen.label || screen.screen_key;
        this.gtwSnackbarService.openSnackBar(
          `Failed to load data for "${label}". Please try again.`,
          'gtw-snackbar--red'
        );
      })
    );
  }

  ngAfterViewInit(): void {
    // All five workspaceFactory streams below are long-lived singletons
    // (the workspace shell stays mounted for the whole session normally,
    // but route remounts during admin/userSettings deep-link transitions
    // would otherwise stack duplicate subscribers). Channel them through
    // the same `subs` bag so `ngOnDestroy` tears every one of them down.
    this.subs.add(
      this.workspaceFactory.screens.subscribe((data: any) => {
        this.workspaces = data;
        // Workspace area can be re-mounted with brand-new screen object
        // references (same `screen_key`, different JS instance) when the
        // user navigates away and back. The cached payloads survive in the
        // `TabGridLoaderService` singleton, but the new screens come in
        // with `cachedData=undefined`, so the tab dots would render gray
        // even though the cached data is one click away. Hydrate the
        // visual state from the loader so the orange (cached) and blinking
        // (loading) dots are accurate from the very first render.
        this.hydrateTabDotStates();
        this.cdr.detectChanges();
        setTimeout(() => this.initScrollableTabs(), 0);
      })
    );

    this.subs.add(
      this.workspaceFactory.setTabActive.subscribe((workspace: any) => {
      // Admin / userSettings breadcrumb sync emits setTabActive while the
      // workspace shell is being torn down; this subscriber is not
      // unsubscribed on destroy, so ignore those links here — otherwise we
      // navigate to `/workspace/admin.*` and block the real `/admin/*` route.
      const screenLink = String(workspace?.link || workspace?.SCREEN_LINK || '');
      if (screenLink.startsWith('admin.') || screenLink.startsWith('userSettings.')) {
        return;
      }

      // Re-hydrate tab dot states and `ws.data` from the TabGridLoader
      // singleton BEFORE DataFilters.initFilters (same setTabActive
      // emission) reads `activeScreen.data`. Without this, a race against
      // the `screens.next` subscriber below can leave `ws.data` null on a
      // returning tab that has a cached payload, causing DataFilters to
      // emit `dataFetched=false` and hiding the grid behind the
      // placeholder.
      this.hydrateTabDotStates();
      this.state = workspace.id;
      this.legacyFilterTag = this.resolveLegacyFilterTag(workspace);

      // The "Modules" home screen (`workspace.modules` / screen_key 10906)
      // is a breadcrumb-only marker — `/workspace/modules` has no real
      // landing page, the home content lives at `/home`. Skip programmatic
      // navigation for it so a stray `setTabActive.next(modulesScreen)`
      // emission (e.g. the one fired from `AppComponent.emitHomeBreadcrumb`
      // after the left-nav menu finishes loading) cannot bounce the user
      // away from their current `/workspace/<slug>` page on a browser
      // refresh.
      const isModulesHome =
        workspace?.screen_key === '10906' || workspace?.link === 'workspace.modules';

      const slug = this.workspaceFactory.getScreenSlug(workspace);
      if (slug && !isModulesHome) {
        // Most screens map to `/workspace/<slug>`, but a small set of
        // legacy top-level uib-router parents (form-view, state-form-view —
        // see WorkspaceService.TOP_LEVEL_LEGACY_STATE_COMMANDS) live OUTSIDE
        // `/workspace/`. Use the helper so tab activation drives the
        // address bar to the same URL the legacy app uses, and so the
        // `startsWith(targetPath)` short-circuit below correctly suppresses
        // duplicate navigations after a deep-link refresh.
        const commands = this.workspaceFactory.getScreenNavigationCommands(workspace);
        const targetPath = this.workspaceFactory.getScreenNavigationPath(workspace);
        if (!this.router.url.startsWith(targetPath)) {
          this.router.navigate(commands, { queryParamsHandling: 'preserve' });
        }
      }

        this.cdr.detectChanges();
        if (!this.hideScrollButtons) {
          setTimeout(() => this.scrollTabIntoView(), 100);
        }
      })
    );

    this.subs.add(
      this.workspaceFactory.dataFetched.subscribe((fetched: boolean) => {
        this.dataFetched = fetched;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.workspaceFactory.isCeScreen.subscribe((isCe: boolean) => {
        this.isCeScreen = isCe;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.workspaceFactory.hideDataFilters.subscribe((hide: boolean) => {
        this.hideDataFilters = hide;
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.workspaceFactory.gridLoading.subscribe((loading: boolean) => {
        this.gridLoading = loading;
        this.cdr.detectChanges();
      })
    );
  }

  onWorkspaceLoad(): void {
    this.workspaceFactory.onLoad();
    this.workspaces = this.workspaceFactory.workspaceFactory.screens;
  }

  backToPreviousScr(): void {
    if (
      this.workspaceFactory.workspaceFactory.goback.workspace &&
      !this.workspaceFactory.workspaceFactory.workflowObj
    ) {
      const goback = this.workspaceFactory.workspaceFactory.goback;
      this.workspaceFactory.loadLink(
        'link', '{}', goback.workspace.activity_key,
        goback.workspace.screen_key, {}, {}, {}, {}, null
      );
    }
  }

  toggleFilters(): void {
    this.workspaceFactory.workspaceFactory.activeScreen.filters?.toggleFilters();
  }

  setTab(_workspace: any, onBackbutton: any): void {
    this.state = _workspace.id;
    this.workspaceFactory.loadTab(_workspace, {}, onBackbutton);

    const slug = this.workspaceFactory.getScreenSlug(_workspace);
    if (slug) {
      // See setTabActive() above — top-level legacy states (form-view, …)
      // resolve to `/<state>/<child>` instead of `/workspace/<slug>`, so
      // route through the WorkspaceService helper to keep both pathways
      // consistent.
      const commands = this.workspaceFactory.getScreenNavigationCommands(_workspace);
      this.router.navigate(commands, { queryParamsHandling: 'preserve' });
    }

    if (this.workspaceFactory.workspaceFactory.goback.workspace) {
      this.isEnable = true;
    }
  }

  updateFn(arg: any): void {
    const obj = this.workspaces.find(
      (link: any) => link.label === arg.tabScrollTitle
    );
    if (obj) {
      this.setTab(obj, null);
    }
  }

  clearScreenCache($event: any, workspace: any): void {
    // Stop propagation so the click on the dot icon does not also trigger
    // the surrounding `<li>` (which would try to switch tabs again).
    $event.stopPropagation();
    if (!workspace) return;
    // Drop both the singleton-loader cache and any in-flight subscription
    // for this screen, so the next visit (or an immediate refresh on the
    // currently-active tab) actually re-fetches.
    this.tabGridLoader.cancelInFlight(workspace.screen_key);
    this.tabGridLoader.clearCache(workspace);
    if (workspace.screen_key === this.workspaceFactory.workspaceFactory.activeScreen?.screen_key) {
      // Same eager flag as DataFilters.refresh: a refetch is about to
      // start, so flip the dot to "loading" before the synchronous
      // refreshGridData → loadGridData → fetchForScreen chain runs the
      // first CD pass.
      workspace.loading_data = true;
      this.workspaceFactory.refreshGridData.next(workspace);
    }
  }

  /**
   * Re-apply per-tab visual indicators (`cachedData` for the orange dot,
   * `loading_data` for the blinking dot) onto the currently-rendered
   * `workspaces` array based on the cross-mount cache and in-flight maps
   * owned by `TabGridLoaderService`. Called whenever `workspaces` is
   * (re-)assigned so the dots are correct immediately, without waiting
   * for the user to click the relevant tab.
   *
   * Also restores `screen.data` from the cache map so consumers that
   * read the payload off the screen object (executive dashboards via
   * `activeScreen.data`, `DataFilters.screenHasSuccessfulGridCache`)
   * see the cached payload on the new screen reference too.
   */
  private hydrateTabDotStates(): void {
    if (!Array.isArray(this.workspaces)) return;
    for (const ws of this.workspaces) {
      if (!ws?.screen_key) continue;
      if (this.tabGridLoader.isLoading(ws.screen_key)) {
        ws.loading_data = true;
        ws.cachedData = false;
        continue;
      }
      const cached = this.tabGridLoader.getCached(ws.screen_key);
      if (cached) {
        ws.cachedData = true;
        ws.loading_data = false;
        if (ws.data == null) {
          ws.data = cached;
        }
      }
    }
  }

  // ---- Scrollable tabset methods ----

  scrollButtonDown(direction: 'left' | 'right', event: MouseEvent): void {
    event.stopPropagation();
    this.isScrollHolding = true;
    const container = this.tabContainerRef?.nativeElement;
    if (!container) return;
    const realScroll = direction === 'left' ? -this.scrollByPixels : this.scrollByPixels;
    this.scrollTo(container, realScroll, 150, () => this.reCalcSides(), true);
    this.scrollIntervalId = setInterval(() => {
      if (this.isScrollHolding) {
        this.scrollTo(container, realScroll, 150, () => this.reCalcSides(), true);
        if ((direction === 'left' && this.disableScrollLeft) ||
            (direction === 'right' && this.disableScrollRight)) {
          this.scrollButtonUp();
        }
      }
    }, 100);
  }

  scrollButtonUp(): void {
    this.isScrollHolding = false;
    if (this.scrollIntervalId) {
      clearInterval(this.scrollIntervalId);
      this.scrollIntervalId = null;
    }
  }

  onTabContainerScroll(): void {
    this.reCalcSides();
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.showDropdownMenu = !this.showDropdownMenu;
    this.cdr.detectChanges();
    if (this.showDropdownMenu) {
      const closeHandler = () => {
        this.showDropdownMenu = false;
        this.cdr.detectChanges();
        document.removeEventListener('click', closeHandler);
      };
      setTimeout(() => document.addEventListener('click', closeHandler), 0);
    }
  }

  activateDropdownTab(tab: any): void {
    this.showDropdownMenu = false;
    this.setTab(tab, null);
    setTimeout(() => this.scrollTabIntoView(), 500);
  }

  private initScrollableTabs(): void {
    this.recalcScrollButtons();
    setTimeout(() => this.scrollTabIntoView(), 500);
    window.removeEventListener('resize', this.resizeHandler);
    window.addEventListener('resize', this.resizeHandler);
  }

  private recalcScrollButtons(): void {
    const container = this.tabContainerRef?.nativeElement;
    if (!container) {
      this.hideScrollButtons = true;
      this.hideDropDown = true;
      return;
    }
    const overflows = container.scrollWidth > container.offsetWidth;
    this.hideScrollButtons = !overflows;
    this.hideDropDown = !overflows;
    if (!this.hideScrollButtons) {
      if (!this.hideDropDown) {
        this.dropdownTabs = (this.workspaces || []).map((ws: any) => ({
          ...ws,
          tabScrollTitle: ws.label
        }));
      } else {
        this.dropdownTabs = [];
      }
      this.reCalcSides();
    } else {
      this.dropdownTabs = [];
    }
    this.cdr.detectChanges();
  }

  private reCalcSides(): void {
    const container = this.tabContainerRef?.nativeElement;
    if (!container || this.hideScrollButtons) return;
    this.disableScrollRight = container.scrollLeft >= container.scrollWidth - container.offsetWidth;
    this.disableScrollLeft = container.scrollLeft <= 0;
    this.cdr.detectChanges();
  }

  private scrollTabIntoView(): void {
    const container = this.tabContainerRef?.nativeElement;
    if (!container || this.hideScrollButtons) return;
    const activeTab = container.querySelector('li.active') as HTMLElement;
    if (!activeTab) return;
    const containerRect = container.getBoundingClientRect();
    const rightPosition = activeTab.getBoundingClientRect().left + activeTab.getBoundingClientRect().width - containerRect.left;
    const leftPosition = activeTab.getBoundingClientRect().left - containerRect.left;
    if (leftPosition < 0) {
      this.scrollTo(container, leftPosition, 700, () => this.reCalcSides());
    } else if (rightPosition > container.offsetWidth) {
      const dif = rightPosition - container.offsetWidth;
      this.scrollTo(container, dif, 700, () => this.reCalcSides());
    }
  }

  private scrollTo(element: HTMLElement, change: number, duration: number, callback: () => void, isLinear = false): void {
    const start = element.scrollLeft;
    const increment = 20;
    const animateScroll = (elapsedTime: number) => {
      elapsedTime += increment;
      let position: number;
      if (isLinear) {
        position = change * elapsedTime / duration + start;
      } else {
        let t = elapsedTime / (duration / 2);
        if (t < 1) {
          position = change / 2 * t * t + start;
        } else {
          t--;
          position = -change / 2 * (t * (t - 2) - 1) + start;
        }
      }
      element.scrollLeft = position;
      if (elapsedTime < duration) {
        setTimeout(() => animateScroll(elapsedTime), increment);
      } else {
        callback();
      }
    };
    animateScroll(0);
  }

  private onWindowResize(): void {
    clearTimeout(this.winResizeTimeout);
    this.winResizeTimeout = setTimeout(() => {
      this.recalcScrollButtons();
      this.scrollTabIntoView();
    }, 250);
  }

  private resolveLegacyFilterTag(workspace: any): string {
    const screenLink = workspace?.link || '';
    const rawLink = screenLink.startsWith('workspace.')
      ? screenLink.substring('workspace.'.length)
      : screenLink;
    return LEGACY_FILTER_PANELS[rawLink] || '';
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    this.scrollButtonUp();
    this.subs.unsubscribe();
  }
}
